import { css } from 'lit';
import * as t from '../styles/tokens.js';
import { hostReset, focusRing } from '../styles/mixins.js';

export default [
  hostReset,
  focusRing('.uwc-gal__item-wrapper', '--uwc-color-primary', '#6366f1'),
  focusRing('.uwc-gal__thumb-item',   '--uwc-color-primary', '#6366f1'),
  css`
    /* ── Host ───────────────────────────────────────────────────────── */
    :host {
      display: block;
    }

    /* ── Outer container ────────────────────────────────────────────── */
    .uwc-gal {
      background: #000;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }

    /* ── Content area (item + thumbnails) ────────────────────────── */
    .uwc-gal__content {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    /* Left / right thumbnail positions → row layout */
    .uwc-gal__content--left,
    .uwc-gal__content--right {
      flex-direction: row;
    }

    /* ── Item section (item-wrapper + optional indicators) ────────── */
    .uwc-gal__item-section {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    /* ── Item wrapper (the focusable/draggable area) ─────────────── */
    .uwc-gal__item-wrapper {
      position: relative;
      flex: 1;
      overflow: hidden;
      user-select: none;
      cursor: grab;
      outline: none;
      min-height: var(--uwc-gal-item-min-height, 300px);
      display: flex;
      flex-direction: column;
    }
    .uwc-gal__item-wrapper:active {
      cursor: grabbing;
    }

    /* Hover-show nav: hide nav areas by default, show on hover/focus-within */
    .uwc-gal__item-wrapper--has-hover-nav .uwc-gal__nav-area {
      opacity: 0;
    }
    .uwc-gal__item-wrapper--has-hover-nav:hover .uwc-gal__nav-area,
    .uwc-gal__item-wrapper--has-hover-nav:focus-within .uwc-gal__nav-area {
      opacity: 1;
    }

    /* Hide fullscreen btn by default, show on wrapper hover/focus */
    .uwc-gal__item-wrapper .uwc-gal__fullscreen-btn {
      opacity: 0;
      transition: opacity ${t.durationBase};
    }
    .uwc-gal__item-wrapper:hover .uwc-gal__fullscreen-btn,
    .uwc-gal__item-wrapper:focus-within .uwc-gal__fullscreen-btn {
      opacity: 1;
    }

    /* ── Single item ─────────────────────────────────────────────── */
    .uwc-gal__item {
      flex: 1;
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* ── Default item image ──────────────────────────────────────── */
    .uwc-gal__default-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      pointer-events: none;
    }

    /* ── Fade animation ──────────────────────────────────────────── */
    @keyframes uwc-gal-fade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    .uwc-gal__item.is-animating {
      animation: uwc-gal-fade var(--uwc-gal-duration, 300ms) ${t.easingOut};
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-gal__item.is-animating {
        animation: none;
      }
    }

    /* ── Nav areas (prev/next overlay buttons) ───────────────────── */
    .uwc-gal__nav-area {
      position: absolute;
      top: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      padding: ${t.space2};
      z-index: 1;
      transition: opacity ${t.durationBase};
    }
    .uwc-gal__nav-area--prev { left: 0; }
    .uwc-gal__nav-area--next { right: 0; }

    /* ── Nav button (native <button> replacing uwc-button) ──────── */
    .uwc-gal__nav-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: ${t.radiusFull};
      border: 1px solid rgba(255,255,255,0.5);
      background: rgba(0,0,0,0.35);
      color: #fff;
      cursor: pointer;
      padding: 0;
      transition:
        background ${t.durationBase},
        border-color ${t.durationBase},
        opacity ${t.durationBase};
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    }
    .uwc-gal__nav-btn:hover:not(:disabled) {
      background: rgba(0,0,0,0.6);
      border-color: rgba(255,255,255,0.8);
    }
    .uwc-gal__nav-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, #6366f1);
      outline-offset: 2px;
    }
    .uwc-gal__nav-btn:disabled,
    .uwc-gal__nav-btn--disabled {
      opacity: 0.35;
      cursor: not-allowed;
      pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-gal__nav-btn { transition: none; }
    }

    /* ── Fullscreen button ───────────────────────────────────────── */
    .uwc-gal__fullscreen-btn {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      z-index: 2;
    }

    /* ── Close button (inside fullscreen dialog) ─────────────────── */
    .uwc-gal__close-btn {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      z-index: 2;
    }

    /* ── Caption overlay ─────────────────────────────────────────── */
    .uwc-gal__caption {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 100%);
      padding: ${t.space6} ${t.space4} ${t.space3};
      pointer-events: none;
    }
    .uwc-gal__caption-title {
      margin: 0 0 ${t.space1};
      color: #fff;
      font-size: ${t.fontSizeXl};
      font-weight: ${t.fontWeightSemibold};
      line-height: 1.3;
    }
    .uwc-gal__caption-desc {
      margin: 0;
      color: rgba(255,255,255,0.75);
      font-size: ${t.fontSizeMd};
      line-height: 1.5;
    }

    /* ── Indicators ──────────────────────────────────────────────── */
    .uwc-gal__indicators {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${t.space2};
      padding: ${t.space2} 0;
      flex-shrink: 0;
    }

    /* Indicators overlaid on the item */
    .uwc-gal__indicators--on-item {
      position: absolute;
      left: 0;
      right: 0;
      z-index: 2;
      padding: ${t.space2};
    }
    .uwc-gal__indicators--on-item.uwc-gal__indicators--top    { top: 0; }
    .uwc-gal__indicators--on-item.uwc-gal__indicators--bottom { bottom: 0; }

    /* Indicator dot button */
    .uwc-gal__indicator {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: ${t.radiusFull};
      border: none;
      padding: 0;
      cursor: pointer;
      transition: background ${t.durationBase}, transform ${t.durationBase}, opacity ${t.durationBase};
      flex-shrink: 0;
      /* Default: dark-on-light (outside the item) */
      background: rgba(0,0,0,0.2);
    }
    .uwc-gal__indicator:hover {
      background: rgba(0,0,0,0.4);
    }
    .uwc-gal__indicator.is-active {
      background: ${t.primary};
      transform: scale(1.35);
    }

    /* On-item: light dots on dark background */
    .uwc-gal__indicators--on-item .uwc-gal__indicator {
      background: rgba(255,255,255,0.45);
    }
    .uwc-gal__indicators--on-item .uwc-gal__indicator:hover {
      background: rgba(255,255,255,0.75);
    }
    .uwc-gal__indicators--on-item .uwc-gal__indicator.is-active {
      background: #fff;
      transform: scale(1.35);
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-gal__indicator { transition: none; }
    }

    /* ── Thumbnail strip ─────────────────────────────────────────── */
    .uwc-gal__thumbnails {
      display: flex;
      align-items: center;
      gap: ${t.space1};
      padding: ${t.space2};
      background: var(--uwc-gal-thumb-bg, #111);
      flex-shrink: 0;
    }
    .uwc-gal__thumbnails--vert {
      flex-direction: column;
      align-self: stretch;
      height: 100%;
    }

    /* Thumbnail viewport (clips the track, hides scrollbar) */
    .uwc-gal__thumb-viewport {
      flex: 1;
      overflow: hidden;
      scrollbar-width: none;
      min-width: 0;
      min-height: 0;
    }
    .uwc-gal__thumb-viewport::-webkit-scrollbar {
      display: none;
    }

    /* Thumbnail track */
    .uwc-gal__thumb-track {
      display: flex;
      gap: ${t.space2};
    }
    .uwc-gal__thumb-track--vert {
      flex-direction: column;
    }

    /* Individual thumbnail button */
    .uwc-gal__thumb-item {
      flex: 0 0 auto;
      width: var(--uwc-gal-thumb-w, 80px);
      height: var(--uwc-gal-thumb-h, 60px);
      overflow: hidden;
      border-radius: ${t.radiusSm};
      cursor: pointer;
      border: 2px solid transparent;
      opacity: 0.6;
      transition:
        opacity      ${t.durationBase},
        border-color ${t.durationBase};
      padding: 0;
      background: none;
    }
    .uwc-gal__thumb-item:hover {
      opacity: 0.9;
    }
    .uwc-gal__thumb-item.is-active {
      border-color: var(--uwc-gal-thumb-active, #fff);
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-gal__thumb-item { transition: none; }
    }

    /* Thumbnail image */
    .uwc-gal__default-thumb {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      pointer-events: none;
    }

    /* ── Fullscreen dialog ───────────────────────────────────────── */
    dialog.uwc-gal__dialog {
      border: none;
      background: #000;
      padding: 0;
      margin: 0;
      width: 100vw;
      max-width: 100vw;
      height: 100dvh;
      max-height: 100dvh;
      display: flex;
      flex-direction: column;
    }

    dialog.uwc-gal__dialog::backdrop {
      background: rgba(0,0,0,0.95);
    }

    /* Inside dialog: make content fill available space */
    dialog.uwc-gal__dialog .uwc-gal__content {
      flex: 1;
      min-height: 0;
    }
    dialog.uwc-gal__dialog .uwc-gal__item-section {
      flex: 1;
      min-height: 0;
    }
    dialog.uwc-gal__dialog .uwc-gal__item-wrapper {
      flex: 1;
      min-height: 0;
      min-width: 0;
    }
    dialog.uwc-gal__dialog .uwc-gal__item {
      height: 100%;
    }
    dialog.uwc-gal__dialog .uwc-gal__default-img {
      object-fit: contain;
    }
  `,
];
